export const DRIVER_LIVE_FRESH_MS = {
  telemetry: 6000,
  interval: 12000,
  currentLap: 180000,
};

export function formatRaceValue(value, { decimals = 3, plus = true } = {}) {
  if (value == null) return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const text = value.toFixed(decimals);
    return plus && value > 0 ? `+${text}` : text;
  }
  const text = String(value).trim();
  return text || '—';
}

export function formatGapToLeader(value, position) {
  if (Number(position) === 1) return 'LEADER';
  if (value == null) return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    // A non-leader with an unexpected zero gap is still not the leader. Show the
    // provider value rather than silently upgrading the car to P1.
    return value > 0 ? `+${value.toFixed(3)}` : value.toFixed(3);
  }
  const text = String(value).trim();
  return text || '—';
}

export function formatIntervalToAhead(value, position) {
  if (Number(position) === 1) return '—';
  if (value == null) return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? `+${value.toFixed(3)}` : value.toFixed(3);
  }
  const text = String(value).trim();
  return text || '—';
}

export function formatLapTime(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${(seconds - minutes * 60).toFixed(3).padStart(6, '0')}`;
}

export function ageFromIso(value, nowMs) {
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? Math.max(0, nowMs - time) : null;
}

export function measurementFreshness(group, nowMs, thresholdMs, { useSourceAge = true } = {}) {
  if (!group?.data) return { availability: 'unavailable', stale: true, ageMs: null };
  const receiptAge = ageFromIso(group.receivedAt, nowMs);
  const sourceAge = useSourceAge ? ageFromIso(group.sourceTime, nowMs) : null;
  const ages = [receiptAge, sourceAge].filter(Number.isFinite);
  const ageMs = ages.length ? Math.max(...ages) : null;
  const stale = group.stale === true || (ageMs != null && thresholdMs != null && ageMs > thresholdMs);
  return { availability: stale ? 'stale' : 'available', stale, ageMs };
}

export function deriveDriverLiveDisplayStatus(snapshot, transportStatus, nowMs) {
  if (!snapshot) {
    if (transportStatus === 'expired') return { key: 'expired', label: 'ACCESS EXPIRED' };
    if (transportStatus === 'recovering') return { key: 'recovering', label: 'RECONNECTING' };
    return { key: 'connecting', label: 'CONNECTING' };
  }
  const telemetry = measurementFreshness(snapshot.telemetry, nowMs, DRIVER_LIVE_FRESH_MS.telemetry);
  if (telemetry.availability === 'unavailable') {
    return { key: 'unavailable', label: 'DATA UNAVAILABLE', telemetry };
  }
  if (telemetry.stale) return { key: 'stale', label: 'STALE DATA', telemetry };
  if (transportStatus === 'recovering') return { key: 'recovering', label: 'LIVE · RECOVERING', telemetry };
  return { key: 'live', label: 'LIVE', telemetry };
}

export function chooseInitialFavourite(drivers, favourite) {
  if (!favourite || !Array.isArray(drivers)) return null;
  return drivers.find(driver => driver.acronym === favourite)?.number ?? null;
}

export function currentLapFreshness(lap, nowMs) {
  return measurementFreshness(lap ? {
    data: lap, receivedAt: lap.receivedAt, stale: lap.stale,
  } : null, nowMs, DRIVER_LIVE_FRESH_MS.currentLap, { useSourceAge: false });
}


export function raceControlLabel(alert) {
  const message = String(alert?.message || '').toUpperCase();

  // Decisions that explicitly reject a penalty/action must be classified before
  // any keyword such as PENALTY or INVESTIGATION is considered.
  if (/\bNO\s+PENALTY\b/.test(message) || /\bNO\s+FURTHER\s+ACTION\b/.test(message) || /\bNO\s+ACTION\b/.test(message)) {
    return 'DECISION';
  }

  if (/\b(?:RESCINDED|WITHDRAWN|CANCELLED|OVERTURNED)\b/.test(message)) return 'DECISION';
  if (/\bUNDER\s+INVESTIGATION\b/.test(message)) return 'INVESTIGATION';
  if (/\b(?:POSSIBLE|POTENTIAL|CONSIDERED|CONSIDERING|REVIEW|INVESTIGATION|MAY|COULD)\b/.test(message)) {
    return 'RACE CONTROL';
  }

  // Only use an affirmative penalty badge for wording that actually communicates
  // an imposed penalty, not merely that a penalty is possible/under review.
  const confirmedPenalty =
    /\b(?:\d+(?:\.\d+)?\s*SECOND(?:S)?\s+(?:TIME\s+)?PENALTY|DRIVE[- ]THROUGH\s+PENALTY|STOP[- ]GO\s+PENALTY)\b/.test(message)
    || /\bPENALTY\s+(?:IMPOSED|APPLIED|FOR)\b/.test(message)
    || /\bPENALTY\s*-\s*\d/.test(message);
  if (confirmedPenalty) return 'PENALTY';

  if (/\bUNDER\s+INVESTIGATION\b/.test(message) || /\bINVESTIGATION\b/.test(message)) return 'INVESTIGATION';
  if (/\bNOTED\b/.test(message)) return 'NOTED';

  return alert?.flag || alert?.category || null;
}

export function availabilityMessage(code, fallback) {
  const known = {
    RACE_NOT_FOUND: ['Race not found', 'This race is not available.'],
    DRIVER_LIVE_CANCELLED: ['Race cancelled', 'Driver Live is not available for a cancelled race.'],
    DRIVER_LIVE_ENDED: ['Race ended', 'Driver Live closes when official results are posted.'],
    DRIVER_LIVE_NOT_OPEN: ['Not open yet', 'Driver Live opens when predictions are locked for the race.'],
    DRIVER_LIVE_UNCONFIGURED: ['Race code not configured', 'Driver Live is unavailable until the organiser configures the race code.'],
    DRIVER_LIVE_NO_SESSION: ['Live data unavailable', 'This race does not have an OpenF1 race session configured.'],
    DRIVER_LIVE_RATE_LIMITED: ['Too many attempts', 'Too many unsuccessful code attempts. Please wait a little before trying again.'],
    REQUEST_TIMEOUT: ['Connection timed out', 'Driver Live did not respond in time. You can retry.'],
    TEMPORARILY_UNAVAILABLE: ['Temporarily unavailable', 'Driver Live could not be reached right now. You can retry.'],
  };
  return known[code] || ['Driver Live unavailable', fallback || 'Live driver data is temporarily unavailable.'];
}
